import { Card, Flex, Input, SimpleTable, Typography } from "djuno-design";
import { ReactComponent as FaceSmile } from "./../../assets/icons/face-smile.svg";
import { useState } from "react";
import ExpandableCard from "../../components/ExpandableCard";
import { GithubTypesLink } from "./../../urls";

const { Title, Text } = Typography;

const InputPage = () => {
  const [inputValue, setInputValue] = useState("");

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Input</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Input component in Djuno Design is a flexible and highly
            customizable form input field that accommodates various use cases,
            ranging from simple text inputs to more advanced configurations.
            With support for labels, error handling, tooltips, hints, and
            optional features like copy-to-clipboard functionality, this
            component is designed to integrate seamlessly into any form or user
            interface. It also provides loading states, size variants, and
            post-input actions to create an optimized and responsive user
            experience.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Input component offers extensive customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Label and Required Field
                </Text>
                : You can attach a label to the input field, along with a
                required indicator (*) to signify mandatory fields.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Error Handling
                </Text>
                : Display error messages beneath the input field with animated
                transitions, ensuring users are clearly informed about
                validation issues.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Hint and Tooltip
                </Text>
                : Add supplementary information through hints, or use tooltips
                for more detailed descriptions without crowding the interface.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Copy-to-Clipboard
                </Text>
                : Enable users to copy the input's value to the clipboard with a
                single click. You can also define a custom function for the copy
                behavior.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading States
                </Text>
                : When the input is loading, a visual indicator (spinner)
                appears, signaling to users that the field is being processed.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Size Variants
                </Text>
                : Choose between different sizes (small, medium, large) to fit
                different layout requirements.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  After Component
                </Text>
                : You can add custom components or icons that appear after the
                input, enhancing the interaction possibilities.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-4">
            Additional Features
          </Title>
          <Text size="sm">
            The Input component comes with the following additional features:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Custom Input Type
                </Text>
                : Support for different input types like default and simple,
                allowing for visual variations based on context.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Interactive Elements
                </Text>
                : Alongside input and label customizations, you can add
                interactive elements like icons or additional components using
                the AfterComponent prop, making the Input more functional.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Placeholder and Tooltip Integration
                </Text>
                : Customizable placeholder text for guiding users and the option
                to integrate tooltips for detailed explanations.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              codeViewerProps={{
                code: InputExample1,
              }}
            >
              <Flex direction="col" className="gap-5 w-full">
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input label="small" uiSize="small" />
                  <Input label="medium" uiSize="medium" />
                  <Input label="large" uiSize="large" />
                </Flex>
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input label="loading" loading loadingType="elastic" />
                </Flex>
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input
                    label="copyable"
                    copyable={{
                      text: (v) => `Hi ${v || ""}`,
                    }}
                    value="djuno-design"
                  />
                </Flex>
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input
                    label="error"
                    loadingType="elastic"
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setInputValue(e.target.value)
                    }
                    error={inputValue === "" ? "Field is required" : ""}
                  />
                </Flex>
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input
                    label="Input"
                    copyable={{
                      icons: [
                        <FaceSmile />,
                        <FaceSmile className="text-green-500" />,
                      ],
                      tooltips: ["Click to copy", "Text copied!"],
                    }}
                  />
                </Flex>
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input label="Input" tooltip={{ content: "I'm a tooltip" }} />
                </Flex>
                <Flex items="end" className="gap-3 w-full flex ">
                  <Input
                    label="Input with custom label"
                    labelClassName="text-green-500 font-bold"
                  />
                </Flex>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* id */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">id</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Unique identifier</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* label */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">label</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The label displayed above or next to the input field.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* inputProps */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">html input props</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Standard HTML attributes passed directly to the
                        underlying {`<input>`} element.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.HTMLProps<HTMLInputElement>`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If true, displays a loading indicator within the input
                        field.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The type of loading indicator to display.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L23"}
                        size="xs"
                        code
                      >
                        LoadingType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the visual style of the input field.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Input.ts#L44"}
                        size="xs"
                        code
                      >
                        InputTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">default</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* placeholder */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">placeholder</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Placeholder text shown inside the input when it is
                        empty.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* required */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">required</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Displays a required * symbol next to the label to
                        indicate the input is mandatory.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* error */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">error</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Type of loading indicator: 'simple' or custom
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | boolean | React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* hint */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">hint</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Provides additional information displayed next to the
                        input label.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tooltip */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tooltip</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Tooltip configuration for the input label, providing
                        more detailed information when hovered or clicked.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L37"}
                        size="xs"
                        code
                      >
                        TooltipProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the size of the input field (height, padding,
                        etc.).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
                {/* copyable */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">copyable</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Enables copy-to-clipboard functionality, allowing users to
                      copy the input value with an optional custom copy
                      behavior.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Typography.Link
                      target="_blank"
                      href={GithubTypesLink + "index.ts#L67"}
                      size="xs"
                      code
                    >
                      {`CopyableProp`}
                    </Typography.Link>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* AfterComponent */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">AfterComponent</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Custom component that appears after the input field,
                      typically for adding icons or buttons.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`React.ReactNode`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* className */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">className</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes to apply custom styles to the
                      input.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* labelClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">labelClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes to apply custom styles to the
                      input's label.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* containerClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">containerClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional classes to apply to the input container.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const InputExample1 = `<Flex direction="col" className="gap-5 w-full">
  <Flex items="end" className="gap-3 w-full flex ">
    <Input label="small" uiSize="small" />
    <Input label="medium" uiSize="medium" />
    <Input label="large" uiSize="large" />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <Input label="loading" loading loadingType="elastic" />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <Input
      label="copyable"
            copyable={(v) => \`Hi \${v}\`}
      value="djuno-design"
    />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <Input
      label="error"
      loadingType="elastic"
      value={inputValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        setInputValue(e.target.value)
      }
      error={inputValue === "" ? "Field is required" : ""}
    />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <Input
      label="Input"
      copyable={{
        icon: [
          <FaceSmile />,
          <FaceSmile className="text-green-500" />,
        ],
        tooltips: ["Click to copy", "Text copied!"],
      }}
    />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <Input label="Input" tooltip={{ content: "I'm a tooltip" }} />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <Input
      label="Input with custom label"
      labelClassName="text-green-500 font-bold"
    />
  </Flex>
</Flex>`;

export default InputPage;
